import React, { useRef, useState } from "react";

import { useMutationObserver } from "@react-hooks-library/core";
import { twJoin } from "tailwind-merge";

type Lines = 3 | 5;

const LINE_CLAMP_CLASSES: Record<Lines, string> = {
  3: "line-clamp-3",
  5: "line-clamp-5",
};

export const ClampedContent: React.FC<{
  children: React.ReactNode;
  lines?: Lines;
  color?: `text-${string}-${number}`;
  fadeToColor?: `to-${string}`;
  bgColor?: `bg-${string}`;
  dataLoc?: string;
}> = ({
  children,
  lines = 5,
  color = "text-gray-600",
  fadeToColor = "to-white",
  bgColor = "bg-white",
  dataLoc = "clamped-content",
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);

  const checkClamped = () => {
    if (!contentRef.current || isExpanded) return;
    setIsClamped(
      contentRef.current.scrollHeight > contentRef.current.clientHeight,
    );
  };

  const setContentRef = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    checkClamped();
  };

  useMutationObserver(contentRef, checkClamped, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return (
    <div className="relative flex min-w-0 flex-col bg-[inherit]">
      <div
        ref={setContentRef}
        className={twJoin(
          "whitespace-pre-wrap break-words",
          isExpanded ? "decideScrollbar" : LINE_CLAMP_CLASSES[lines],
        )}
        data-loc={dataLoc}
        data-record="true"
      >
        {children}
      </div>
      {isClamped &&
        (isExpanded ? (
          <button
            className={twJoin("self-end underline", color)}
            onClick={() => setIsExpanded(false)}
          >
            See less
          </button>
        ) : (
          <span className="absolute right-0 bottom-0 flex items-center">
            <span
              className={twJoin(
                "h-5 w-12 bg-gradient-to-r from-transparent",
                fadeToColor,
              )}
            />
            <button
              className={twJoin("pl-1 underline", color, bgColor)}
              onClick={() => setIsExpanded(true)}
            >
              See more
            </button>
          </span>
        ))}
    </div>
  );
};
